"use client";

import { useReducedMotion } from "@/lib/motion";

/* Atmospheric electric-blue mesh behind hero sections.
 * Three elements, two breathing slightly off-cycle:
 *  1. top-left   electric blue cone — primary atmosphere
 *  2. top-right  deep violet undertone — adds chromatic depth
 *  3. bottom     blue rim light — keeps the lower fold from feeling dead
 *
 * Pointer-events: none. Sits behind content (zIndex 0). All three respect
 * prefers-reduced-motion via the useReducedMotion hook.
 */
export function GradientMesh({
  intensity = 1,
}: {
  intensity?: number;
}) {
  const rm = useReducedMotion();
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {/* 1 · Primary electric-blue cone — top-left, big, breathing */}
      <div
        className="absolute"
        style={{
          top: "-30%",
          left: "-15%",
          width: "100%",
          height: "130%",
          background:
            "radial-gradient(ellipse at center, rgba(184,115,51,0.55), rgba(184,115,51,0.12) 35%, transparent 65%)",
          filter: "blur(80px)",
          opacity: 0.55 * intensity,
          animation: rm
            ? "none"
            : "ax-mesh-breathe 11s ease-in-out infinite",
        }}
      />

      {/* 2 · Deep blue undertone — top-right, slow, subtle.
       *    Was violet (80,40,200); palette is locked to black + blue only. */}
      <div
        className="absolute"
        style={{
          top: "-25%",
          right: "-25%",
          width: "85%",
          height: "110%",
          background:
            "radial-gradient(ellipse at center, rgba(0,80,210,0.42), transparent 60%)",
          filter: "blur(110px)",
          opacity: 0.4 * intensity,
          animation: rm
            ? "none"
            : "ax-mesh-breathe 13s ease-in-out -3s infinite",
        }}
      />

      {/* 3 · Bottom rim light — slim electric blue beam from below */}
      <div
        className="absolute"
        style={{
          bottom: "-40%",
          left: "10%",
          width: "70%",
          height: "60%",
          background:
            "radial-gradient(ellipse at center, rgba(184,115,51,0.32), transparent 55%)",
          filter: "blur(90px)",
          opacity: 0.32 * intensity,
          animation: rm
            ? "none"
            : "ax-mesh-breathe 17s ease-in-out -7s infinite",
        }}
      />

      {/* 4 · Vignette — pulls focus toward center, kills mid-canvas flatness */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, transparent 35%, rgba(10,10,10,0.55) 90%)",
          opacity: 0.6 * intensity,
        }}
      />
    </div>
  );
}
